import { baseApi } from "../../../store/api/baseApi";
import type { PerformanceConfig } from "../../../../shared/products/performance/config";
import type {
  NotificationPrefsInput,
  NotificationPrefsView,
} from "../../../../shared/products/performance/notificationPrefs";
import type {
  AcknowledgeRecordFields,
  AcknowledgeRecordResult,
  CoachingAttachmentUrl,
  CoachingRecord,
  CoachingRecordList,
  CoachingSubjectCandidate,
  CorrectCoachingRecordFields,
  CorrectCoachingRecordResult,
  CreateCoachingRecordFields,
  CreateCoachingRecordResult,
  PerformanceScope,
  PersonAccessUpdate,
  PerformanceSettings,
  PersonFile,
  RemindRecordResult,
  RolloutLocations,
  SaveRolloutRequest,
  SaveRolloutResponse,
  SaveSettingsRequest,
  SaveSettingsResponse,
  PositionRow,
  RecomputePreview,
  RecomputeResult,
  CreateTodoFields,
  OrgChart,
  CreateTodoResult,
  Todo,
  TodoList,
  TodoNote,
  TodoOwners,
  TodoSummary,
  AcknowledgePlanFields,
  ActivatePlanFields,
  ActivatePlanResult,
  AddPlanActionFields,
  AddPlanActionResult,
  CreatePlanFields,
  DecidePlanResult,
  EditPlanFields,
  Plan,
  PlanApproverCandidate,
  PlanCommentResult,
  PlanChainPreview,
  PlanEvidenceRead,
  PlanList,
  PlanReminderFields,
  PlanSubjectCandidate,
  ResolvePlanFields,
  ResolvePlanResult,
  SubmitPlanResult,
  TerminationFields,
  Evaluation,
  EvaluationDraftPatch,
  EvaluationList,
  EvaluationSubjectCandidate,
  EvaluationTab,
  DocumentList,
  EmployeeFileExportRequest,
  ExportLog,
  NotificationList,
  SelfEvaluation,
  RecordsList,
  RecordsListRow,
  Visit,
  VisitList,
  VisitPatch,
  VisitStartOptions,
  VisitTab,
} from "../../../../shared/products/performance/api";
import type { MyPermissions } from "../../../../shared/types/rbac";
import type { CoachingRecordsQuery, PerfPersonRow, PlansQuery, TodosQuery } from "./types";

/** The query string of a coaching list call, with the unset filters left out. */
const definedParams = (params: Record<string, string | number | undefined>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""));

/** A correction's multipart form: the new version's fields and its own files (E1.c). */
function correctionForm(
  fields: CorrectCoachingRecordFields,
  files: readonly File[] = [],
): FormData {
  const form = new FormData();
  form.append("title", fields.title);
  form.append("body", fields.body);
  form.append("conversationDate", fields.conversationDate);
  form.append("correctionNote", fields.correctionNote);
  for (const file of files) form.append("files", file, file.name);
  return form;
}

/** Multipart: the fields, repeated `subjects` for the list, and up to five `files`. */
function coachingForm(fields: CreateCoachingRecordFields, files: readonly File[] = []): FormData {
  const form = new FormData();
  form.append("typeKey", fields.typeKey);
  for (const subject of fields.subjects) form.append("subjects", subject);
  form.append("title", fields.title);
  form.append("body", fields.body);
  form.append("conversationDate", fields.conversationDate);
  for (const file of files) form.append("files", file, file.name);
  return form;
}

/**
 * E7 review P2-2: the org chart's cached entry carries file counts (records, open to-dos) and
 * levels, so the writes that change them refetch it.
 */
const ORG_CHART = { type: "Performance" as const, id: "ORG_CHART" };

/** E3: the plan list (and its counts, the nav badge's source) and one plan. */
const PLAN_LIST = { type: "Performance" as const, id: "PLAN_LIST" };
const planTag = (planId: string) => ({ type: "Performance" as const, id: `PLAN:${planId}` });
const TODO_LIST = { type: "Performance" as const, id: "TODO_LIST" };
/** E4: the evaluation list (and Home's counts) and one evaluation. */
const EVAL_LIST = { type: "Performance" as const, id: "EVAL_LIST" };
const evalTag = (evaluationId: string) => ({
  type: "Performance" as const,
  id: `EVAL:${evaluationId}`,
});
const TODO_COUNTS = { type: "Performance" as const, id: "TODO_COUNTS" };
/** E5: the visit list and one visit. */
const VISIT_LIST = { type: "Performance" as const, id: "VISIT_LIST" };
const visitTag = (visitId: string) => ({ type: "Performance" as const, id: `VISIT:${visitId}` });
/** E8: the export log (each export adds a row). */
const EXPORT_LOG = { type: "Performance" as const, id: "EXPORT_LOG" };
/** A file the server built: the bytes and its name. Errors come back as JSON. */
const fileResponse = (response: Response) =>
  response.ok ? response.blob() : response.json().catch(() => null);
/** The file's name: `filename*` (UTF-8, any script) first, then `filename` (E8 final review C2). */
export function dispositionName(header: string | null | undefined, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header ?? "")?.[1];
  if (star) {
    try {
      return decodeURIComponent(star);
    } catch {
      // A malformed name falls back to the plain one.
    }
  }
  return /filename="([^"]+)"/.exec(header ?? "")?.[1] ?? fallback;
}
const asFile = (fallback: string) => (blob: Blob, meta: unknown) => ({
  blob,
  filename: dispositionName(
    (meta as { response?: Response } | undefined)?.response?.headers.get("Content-Disposition"),
    fallback,
  ),
});
/**
 * E3.b (§4.6): a write that raises to-dos or changes what is live on a file refetches the plan,
 * the list, the to-dos, the subject's file header (it counts live plans) and the chart (its flag).
 */
const planWriteTags = (planId: string, subjectKey: string | undefined) => [
  PLAN_LIST,
  planTag(planId),
  TODO_LIST,
  TODO_COUNTS,
  ORG_CHART,
  ...(subjectKey ? [{ type: "Performance" as const, id: `FILE:${subjectKey}` }] : []),
];

/**
 * skyPerformance's own endpoints on the shared baseApi (spec §2: it defines its own RTK endpoints
 * and imports no hire endpoint module). Every url is under /performance, so baseApi's instance-id
 * injection and the PRODUCT_DISABLED watcher (Task 13) apply, and the browser activity tracker
 * leaves them to the server's product-tagged audit.
 *
 * A refusal (403 ROSTER_TERMINATED, 404 when the product is not enabled) is not retried and comes
 * back as the query's or mutation's `error`; the screens decide what to show. Nothing here grants
 * access: the server gates every route.
 */
export const performanceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPerformanceConfig: builder.query<{ config: PerformanceConfig }, string>({
      query: (instanceId) => ({
        url: "/performance/config",
        params: { instanceId },
      }),
      providesTags: [{ type: "Performance", id: "CONFIG" }],
    }),
    getMyPerformanceScope: builder.query<PerformanceScope, string>({
      query: (instanceId) => ({
        url: "/performance/me/scope",
        params: { instanceId },
      }),
      providesTags: [{ type: "Performance", id: "SCOPE" }],
    }),
    // A UI hint for which performance pages to show; the server decides every request itself.
    getMyPerformancePermissions: builder.query<MyPermissions, string>({
      query: (instanceId) =>
        `/admin/rbac/permissions/my-permissions?instanceId=${encodeURIComponent(instanceId)}&productKey=skyperformance`,
      providesTags: [{ type: "Performance", id: "MY_PERMISSIONS" }],
    }),
    getPositions: builder.query<{ positions: PositionRow[]; preview: RecomputePreview }, string>({
      query: (instanceId) => ({
        url: "/performance/positions",
        params: { instanceId },
      }),
      providesTags: [{ type: "Performance", id: "POSITIONS" }],
    }),
    savePositions: builder.mutation<
      // `preview` is null when the save committed but the follow-up preview failed.
      { updated: number; preview: RecomputePreview | null },
      {
        instanceId: string;
        positions: Array<Pick<PositionRow, "positionKey" | "roleName" | "levelKey">>;
      }
    >({
      query: ({ instanceId, positions }) => ({
        url: "/performance/positions",
        method: "PUT",
        params: { instanceId },
        body: { positions },
      }),
      invalidatesTags: [{ type: "Performance", id: "POSITIONS" }],
    }),
    recompute: builder.mutation<RecomputeResult, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/performance/admin/recompute",
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: [
        { type: "Performance", id: "POSITIONS" },
        { type: "Performance", id: "SCOPE" },
        { type: "RBAC", id: "USERS" },
        ORG_CHART,
      ],
    }),
    /** E9: what the Settings page edits (HR): resolved config, pack, overrides, asOf. */
    getPerformanceSettings: builder.query<PerformanceSettings, string>({
      query: (instanceId) => ({
        url: "/performance/settings",
        params: { instanceId },
      }),
      providesTags: [{ type: "Performance", id: "SETTINGS" }],
    }),
    saveSettings: builder.mutation<
      SaveSettingsResponse,
      { instanceId: string } & SaveSettingsRequest
    >({
      query: ({ instanceId, settings, ifUnchangedSince }) => ({
        url: "/performance/settings",
        method: "PUT",
        params: { instanceId },
        body: ifUnchangedSince === undefined ? { settings } : { settings, ifUnchangedSince },
      }),
      // Never re-sent: a save whose answer was lost has landed, and a re-send would trip the
      // concurrent-edit check against its own write.
      extraOptions: { maxRetries: 0 },
      // The sidebar, every label reader and the editor all refetch (E9 §3.3.4).
      invalidatesTags: [
        { type: "Performance", id: "CONFIG" },
        { type: "Performance", id: "SETTINGS" },
        { type: "Performance", id: "SCOPE" },
      ],
    }),
    /** Spec 2026-10-19 §1: the roster's locations with head counts (HR). */
    getRolloutLocations: builder.query<RolloutLocations, string>({
      query: (instanceId) => ({
        url: "/performance/settings/rollout/locations",
        params: { instanceId },
      }),
      providesTags: [{ type: "Performance", id: "ROLLOUT" }],
    }),
    /** Who has Performance Management; the server recomputes roles after it saves. */
    saveRollout: builder.mutation<SaveRolloutResponse, { instanceId: string } & SaveRolloutRequest>(
      {
        query: ({ instanceId, liveLocations, ifUnchangedSince }) => ({
          url: "/performance/settings/rollout",
          method: "PUT",
          params: { instanceId },
          body:
            ifUnchangedSince === undefined
              ? { liveLocations }
              : { liveLocations, ifUnchangedSince },
        }),
        // Never re-sent: a save whose answer was lost has landed (as saveSettings).
        extraOptions: { maxRetries: 0 },
        // Roles move: the editor, every reader of the config, the Users page and the chart.
        invalidatesTags: [
          { type: "Performance", id: "CONFIG" },
          { type: "Performance", id: "SETTINGS" },
          { type: "Performance", id: "SCOPE" },
          { type: "Performance", id: "ROLLOUT" },
          { type: "Performance", id: "POSITIONS" },
          { type: "RBAC", id: "USERS" },
          ORG_CHART,
        ],
      },
    ),
    /** Spec 2026-10-19 §2: the caller's own notification channel (every Performance role). */
    getNotificationPrefs: builder.query<NotificationPrefsView, string>({
      query: (instanceId) => ({
        url: "/performance/me/notification-prefs",
        params: { instanceId },
      }),
      providesTags: [{ type: "Performance", id: "NOTIFICATION_PREFS" }],
    }),
    saveNotificationPrefs: builder.mutation<
      NotificationPrefsView,
      { instanceId: string; prefs: NotificationPrefsInput }
    >({
      query: ({ instanceId, prefs }) => ({
        url: "/performance/me/notification-prefs",
        method: "PUT",
        params: { instanceId },
        body: prefs,
      }),
      invalidatesTags: [{ type: "Performance", id: "NOTIFICATION_PREFS" }],
    }),
    getPerson: builder.query<{ person: PerfPersonRow }, { instanceId: string; personKey: string }>({
      query: ({ instanceId, personKey }) => ({
        url: `/performance/people/${encodeURIComponent(personKey)}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { personKey }) => [{ type: "Performance", id: `PERSON:${personKey}` }],
    }),
    /** Ruling 2026-10-12: HR sets who someone reports to; null hands them back to the HRIS. */
    setPersonManager: builder.mutation<
      { ok: true; recomputed: boolean; previous: string | null },
      { instanceId: string; personKey: string; managerPersonKey: string | null }
    >({
      query: ({ instanceId, personKey, managerPersonKey }) => ({
        url: `/performance/people/${encodeURIComponent(personKey)}/manager`,
        method: "PUT",
        params: { instanceId },
        body: { managerPersonKey },
      }),
      // The line moves: every file (its "Reports to" and reach), the chart and the caller's scope;
      // roles are recomputed, so the Users page, the people's drawers, positions and the visit
      // leaders (perf_managers) too (review I3).
      invalidatesTags: (result, _e, { personKey, managerPersonKey }) => [
        { type: "Performance", id: "FILE" },
        ORG_CHART,
        { type: "Performance", id: "SCOPE" },
        { type: "RBAC", id: "USERS" },
        { type: "Performance", id: "POSITIONS" },
        VISIT_LIST,
        { type: "Performance", id: `PERSON:${personKey}` },
        ...(managerPersonKey
          ? [{ type: "Performance" as const, id: `PERSON:${managerPersonKey}` }]
          : []),
        // The manager they reported to before.
        ...(result?.previous
          ? [{ type: "Performance" as const, id: `PERSON:${result.previous}` }]
          : []),
      ],
    }),
    putPersonAccess: builder.mutation<
      { person: PerfPersonRow; assignment: { id: string } | null },
      { instanceId: string; personKey: string; update: PersonAccessUpdate }
    >({
      query: ({ instanceId, personKey, update }) => ({
        url: `/performance/people/${encodeURIComponent(personKey)}/access`,
        method: "PUT",
        params: { instanceId },
        body: update,
      }),
      invalidatesTags: (_r, _e, { personKey }) => [
        { type: "Performance", id: `PERSON:${personKey}` },
        { type: "RBAC", id: "USERS" },
        { type: "Performance", id: "SCOPE" },
        // A level override changes the level the chart shows.
        ORG_CHART,
      ],
    }),
    // Ruling 2026-10-14: HR's "Can run site visits" for a manager below the run level.
    putVisitsGrant: builder.mutation<
      { person: PerfPersonRow | null },
      { instanceId: string; personKey: string; grant: boolean }
    >({
      query: ({ instanceId, personKey, grant }) => ({
        url: `/performance/people/${encodeURIComponent(personKey)}/visits-grant`,
        method: "PUT",
        params: { instanceId },
        body: { grant },
      }),
      invalidatesTags: (_r, _e, { personKey }) => [
        { type: "Performance", id: `PERSON:${personKey}` },
        { type: "RBAC", id: "USERS" },
        { type: "Performance", id: "SCOPE" },
        VISIT_LIST,
      ],
    }),
    // ── Coaching (E1) ──
    getCoachingSubjects: builder.query<
      { candidates: CoachingSubjectCandidate[] },
      { instanceId: string; q?: string }
    >({
      query: ({ instanceId, q }) => ({
        url: "/performance/coaching/subjects",
        params: definedParams({ instanceId, q }),
      }),
    }),
    getCoachingRecords: builder.query<CoachingRecordList, CoachingRecordsQuery>({
      query: ({ instanceId, ...filters }) => ({
        url: "/performance/coaching/records",
        params: definedParams({ instanceId, ...filters }),
      }),
      providesTags: [{ type: "Performance", id: "COACHING_LIST" }],
    }),
    /**
     * E1 O-2: every record matching the log's current filters, as CSV. A GET, run as a mutation so
     * it is fetched on click and never cached, and never re-sent (`maxRetries: 0`: each one is
     * audited). The text comes back as a string (serialisable), with the server's file name.
     */
    exportCoachingRecords: builder.mutation<
      { csv: string; filename: string },
      Omit<CoachingRecordsQuery, "page" | "pageSize">
    >({
      query: ({ instanceId, ...filters }) => ({
        url: "/performance/coaching/records/export.csv",
        params: definedParams({ instanceId, ...filters }),
        // A refusal is JSON with a code; the file is text.
        responseHandler: (response) =>
          response.ok ? response.text() : response.json().catch(() => null),
      }),
      transformResponse: (csv: string, meta) => ({
        csv,
        filename:
          /filename="([^"]+)"/.exec(
            (meta as { response?: Response } | undefined)?.response?.headers.get(
              "Content-Disposition",
            ) ?? "",
          )?.[1] ?? "coaching-records.csv",
      }),
      extraOptions: { maxRetries: 0 },
    }),
    /** "Select my whole team" (D13): the caller's branch, up to the subject limit. */
    getCoachingTeam: builder.query<
      { candidates: CoachingSubjectCandidate[]; teamSize: number },
      string
    >({
      query: (instanceId) => ({
        url: "/performance/coaching/subjects",
        params: { instanceId, team: "1" },
      }),
    }),
    /** A short-lived download URL; fetched on click, never cached (each one is audited). */
    getCoachingAttachmentUrl: builder.query<
      CoachingAttachmentUrl,
      { instanceId: string; recordId: string; attachmentId: string }
    >({
      query: ({ instanceId, recordId, attachmentId }) => ({
        url: `/performance/coaching/records/${encodeURIComponent(recordId)}/attachments/${encodeURIComponent(attachmentId)}`,
        params: { instanceId },
      }),
      keepUnusedDataFor: 0,
    }),
    getCoachingRecord: builder.query<
      { record: CoachingRecord },
      { instanceId: string; recordId: string }
    >({
      query: ({ instanceId, recordId }) => ({
        url: `/performance/coaching/records/${encodeURIComponent(recordId)}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { recordId }) => [{ type: "Performance", id: `COACHING:${recordId}` }],
    }),
    createCoachingRecord: builder.mutation<
      CreateCoachingRecordResult,
      { instanceId: string; fields: CreateCoachingRecordFields; files?: readonly File[] }
    >({
      query: ({ instanceId, fields, files }) => ({
        url: "/performance/coaching/records",
        method: "POST",
        params: { instanceId },
        body: coachingForm(fields, files),
      }),
      // Never re-sent: a filing that reached the server and lost its answer would file twice.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [
        { type: "Performance", id: "COACHING_LIST" },
        { type: "Performance", id: "FILE" },
        ORG_CHART,
      ],
    }),
    // ── Coaching corrections and deletion (E1.c) ──
    correctCoachingRecord: builder.mutation<
      CorrectCoachingRecordResult,
      {
        instanceId: string;
        recordId: string;
        fields: CorrectCoachingRecordFields;
        files?: readonly File[];
      }
    >({
      query: ({ instanceId, recordId, fields, files }) => ({
        url: `/performance/coaching/records/${encodeURIComponent(recordId)}/versions`,
        method: "POST",
        params: { instanceId },
        body: correctionForm(fields, files),
      }),
      // Never re-sent: a correction whose answer was lost would add two versions.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { recordId }) => [
        { type: "Performance", id: "COACHING_LIST" },
        { type: "Performance", id: `COACHING:${recordId}` },
        // E2: a file's header counts what is on it.
        { type: "Performance", id: "FILE" },
        ORG_CHART,
      ],
    }),
    deleteCoachingRecord: builder.mutation<
      { deleted: true },
      { instanceId: string; recordId: string; reason: string }
    >({
      query: ({ instanceId, recordId, reason }) => ({
        url: `/performance/coaching/records/${encodeURIComponent(recordId)}`,
        method: "DELETE",
        params: { instanceId },
        body: { reason },
      }),
      // Not re-sent on a lost answer; a second try that finds it deleted is 409 ALREADY_DELETED,
      // which the page treats as done.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { recordId }) => [
        { type: "Performance", id: "COACHING_LIST" },
        { type: "Performance", id: `COACHING:${recordId}` },
        // E2: a file's header counts what is on it.
        { type: "Performance", id: "FILE" },
        ORG_CHART,
      ],
    }),
    restoreCoachingRecord: builder.mutation<
      { restored: true },
      { instanceId: string; recordId: string }
    >({
      query: ({ instanceId, recordId }) => ({
        url: `/performance/coaching/records/${encodeURIComponent(recordId)}/restore`,
        method: "POST",
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { recordId }) => [
        { type: "Performance", id: "COACHING_LIST" },
        { type: "Performance", id: `COACHING:${recordId}` },
        // E2: a file's header counts what is on it.
        { type: "Performance", id: "FILE" },
        ORG_CHART,
      ],
    }),
    // ── Acknowledgement (E2) ──
    acknowledgeCoachingRecord: builder.mutation<
      AcknowledgeRecordResult,
      { instanceId: string; recordId: string; fields: AcknowledgeRecordFields }
    >({
      query: ({ instanceId, recordId, fields }) => ({
        url: `/performance/coaching/records/${encodeURIComponent(recordId)}/acknowledge`,
        method: "POST",
        params: { instanceId },
        body: fields,
      }),
      // Never re-sent: a lost answer must not acknowledge twice. A 409 ALREADY_ACKNOWLEDGED on a
      // manual retry means it landed.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { recordId }) => [
        { type: "Performance", id: "COACHING_LIST" },
        { type: "Performance", id: `COACHING:${recordId}` },
        // E2: a file's header counts what is on it.
        { type: "Performance", id: "FILE" },
      ],
    }),
    /** D8: email everyone still waiting. Not re-sent: a lost answer may have sent the emails. */
    remindCoachingRecord: builder.mutation<
      RemindRecordResult,
      { instanceId: string; recordId: string }
    >({
      query: ({ instanceId, recordId }) => ({
        url: `/performance/coaching/records/${encodeURIComponent(recordId)}/remind`,
        method: "POST",
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { recordId }) => [
        { type: "Performance", id: `COACHING:${recordId}` },
      ],
    }),
    /** The employee file's header (D6); the tabs read their own lists. */
    getPersonFile: builder.query<PersonFile, { instanceId: string; personKey: string }>({
      query: ({ instanceId, personKey }) => ({
        url: `/performance/people/${encodeURIComponent(personKey)}/file`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { personKey }) => [
        { type: "Performance", id: "FILE" },
        { type: "Performance", id: `FILE:${personKey}` },
      ],
    }),
    // ── Org chart (E7) ──
    /**
     * The whole active chart, slim (D3). Kept five minutes; refetched after the writes that change
     * its counts or levels (ORG_CHART above). The roster itself changes only by the nightly sync.
     */
    getOrgChart: builder.query<OrgChart, string>({
      query: (instanceId) => ({ url: "/performance/org/chart", params: { instanceId } }),
      keepUnusedDataFor: 300,
      providesTags: [ORG_CHART],
    }),
    /** #1334: the whole chart as CSV (views-org.js:209); the server logs the export. */
    exportOrgChart: builder.mutation<{ csv: string; filename: string }, string>({
      query: (instanceId) => ({
        url: "/performance/org/chart/export.csv",
        params: { instanceId },
        responseHandler: (response) =>
          response.ok ? response.text() : response.json().catch(() => null),
      }),
      transformResponse: (csv: string, meta) => ({
        csv,
        filename:
          /filename="([^"]+)"/.exec(
            (meta as { response?: Response } | undefined)?.response?.headers.get(
              "Content-Disposition",
            ) ?? "",
          )?.[1] ?? "org-chart.csv",
      }),
      extraOptions: { maxRetries: 0 },
    }),
    // ── To-dos (E6) ──
    /** A tab's rows and the badge/glance counts; `pageSize: 0` is counts only (the nav badge). */
    getTodos: builder.query<TodoList, TodosQuery>({
      query: ({ instanceId, ...filters }) => ({
        url: "/performance/todos",
        params: definedParams({ instanceId, ...filters }),
      }),
      providesTags: [
        { type: "Performance", id: "TODO_LIST" },
        { type: "Performance", id: "TODO_COUNTS" },
      ],
    }),
    /** One item with its notes; every open is audited on the server. */
    getTodo: builder.query<{ todo: Todo }, { instanceId: string; todoId: string }>({
      query: ({ instanceId, todoId }) => ({
        url: `/performance/todos/${encodeURIComponent(todoId)}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { todoId }) => [{ type: "Performance", id: `TODO:${todoId}` }],
    }),
    /** D16: who the caller may assign to, themselves first; capped, `more` when cut (#1432). */
    getTodoOwners: builder.query<TodoOwners, { instanceId: string; q?: string }>({
      query: ({ instanceId, q }) => ({
        url: "/performance/todos/owners",
        params: definedParams({ instanceId, q }),
      }),
    }),
    createTodo: builder.mutation<
      CreateTodoResult,
      { instanceId: string; fields: CreateTodoFields }
    >({
      query: ({ instanceId, fields }) => ({
        url: "/performance/todos",
        method: "POST",
        params: { instanceId },
        body: fields,
      }),
      // Never re-sent: a create whose answer was lost would raise the item twice.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { fields }) => [
        { type: "Performance", id: "TODO_LIST" },
        { type: "Performance", id: "TODO_COUNTS" },
        // The owner's file header counts open to-dos.
        { type: "Performance", id: `FILE:${fields.ownerPersonKey}` },
        ORG_CHART,
      ],
    }),
    addTodoNote: builder.mutation<
      { note: TodoNote },
      { instanceId: string; todoId: string; body: string }
    >({
      query: ({ instanceId, todoId, body }) => ({
        url: `/performance/todos/${encodeURIComponent(todoId)}/notes`,
        method: "POST",
        params: { instanceId },
        body: { body },
      }),
      // Never re-sent: a lost answer must not add the note twice.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { todoId }) => [
        { type: "Performance", id: "TODO_LIST" },
        { type: "Performance", id: "TODO_COUNTS" },
        { type: "Performance", id: `TODO:${todoId}` },
      ],
    }),
    /** Closes with its note (R6). A retry that finds it closed is 409 TODO_CLOSED. */
    closeTodo: builder.mutation<
      { todo: TodoSummary },
      { instanceId: string; todoId: string; note: string }
    >({
      query: ({ instanceId, todoId, note }) => ({
        url: `/performance/todos/${encodeURIComponent(todoId)}/close`,
        method: "POST",
        params: { instanceId },
        body: { note },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { todoId }) => [
        { type: "Performance", id: "TODO_LIST" },
        { type: "Performance", id: "TODO_COUNTS" },
        { type: "Performance", id: `TODO:${todoId}` },
        ORG_CHART,
        ...(r ? [{ type: "Performance" as const, id: `FILE:${r.todo.owner.personKey}` }] : []),
        // E3.b (acceptance 2): a plan's action IS its to-do, so closing it ticks the plan.
        ...(r?.todo.source?.type === "plan" && r.todo.source.id
          ? [PLAN_LIST, planTag(r.todo.source.id)]
          : []),
      ],
    }),
    // ── Improvement plans (E3.a) ──
    /** A tab's rows and the tab/glance/badge counts; `pageSize: 0` is counts only. */
    getPlans: builder.query<PlanList, PlansQuery>({
      query: ({ instanceId, ...filters }) => ({
        url: "/performance/plans",
        params: definedParams({ instanceId, ...filters }),
      }),
      providesTags: [PLAN_LIST],
    }),
    /** One plan; every open is audited on the server. */
    getPlan: builder.query<{ plan: Plan }, { instanceId: string; planId: string }>({
      query: ({ instanceId, planId }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { planId }) => [planTag(planId)],
    }),
    /** O-1: who the caller may start a plan on, with the evidence that would attach. */
    getPlanSubjects: builder.query<
      { candidates: PlanSubjectCandidate[] },
      // E3.b: `subject` narrows to one person (the file's Start button preselects them).
      { instanceId: string; q?: string; subject?: string }
    >({
      query: ({ instanceId, q, subject }) => ({
        url: "/performance/plans/subjects",
        params: definedParams({ instanceId, q, subject }),
      }),
    }),
    /** O-2: what submit would compute for the caller and this subject now. */
    getPlanChain: builder.query<PlanChainPreview, { instanceId: string; subject: string }>({
      query: ({ instanceId, subject }) => ({
        url: "/performance/plans/chain",
        params: { instanceId, subject },
      }),
      providesTags: [PLAN_LIST],
    }),
    /** O-3: managers and HR the starter may add, minus the subject, the starter and the levels. */
    getPlanApprovers: builder.query<
      { candidates: PlanApproverCandidate[] },
      { instanceId: string; subject: string; q?: string }
    >({
      query: ({ instanceId, subject, q }) => ({
        url: "/performance/plans/approvers",
        params: definedParams({ instanceId, subject, q }),
      }),
    }),
    /** D25: an attached record's pinned version, read through the plan (audited each time). */
    getPlanEvidence: builder.query<
      { record: PlanEvidenceRead },
      { instanceId: string; planId: string; recordId: string }
    >({
      query: ({ instanceId, planId, recordId }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/evidence/${encodeURIComponent(recordId)}`,
        params: { instanceId },
      }),
      keepUnusedDataFor: 0,
    }),
    createPlan: builder.mutation<{ plan: Plan }, { instanceId: string; fields: CreatePlanFields }>({
      query: ({ instanceId, fields }) => ({
        url: "/performance/plans",
        method: "POST",
        params: { instanceId },
        body: fields,
      }),
      // Never re-sent: a create whose answer was lost would open the plan twice.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [PLAN_LIST],
    }),
    updatePlan: builder.mutation<
      { plan: Plan },
      { instanceId: string; planId: string; fields: EditPlanFields }
    >({
      query: ({ instanceId, planId, fields }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}`,
        method: "PUT",
        params: { instanceId },
        body: fields,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { planId }) => [PLAN_LIST, planTag(planId)],
    }),
    setPlanApprovers: builder.mutation<
      { plan: Plan },
      { instanceId: string; planId: string; personKeys: string[] }
    >({
      query: ({ instanceId, planId, personKeys }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/approvers`,
        method: "PUT",
        params: { instanceId },
        body: { personKeys },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { planId }) => [planTag(planId)],
    }),
    savePlanTermination: builder.mutation<
      { plan: Plan },
      { instanceId: string; planId: string; fields: TerminationFields }
    >({
      query: ({ instanceId, planId, fields }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/termination`,
        method: "PUT",
        params: { instanceId },
        body: fields,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { planId }) => [planTag(planId)],
    }),
    submitPlan: builder.mutation<SubmitPlanResult, { instanceId: string; planId: string }>({
      query: ({ instanceId, planId }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/submit`,
        method: "POST",
        params: { instanceId },
        body: {},
      }),
      // Never re-sent: a lost answer must not start a second round.
      extraOptions: { maxRetries: 0 },
      // Spec 2026-10-13 §4: nothing to wait on starts a plan below the final level (its to-dos).
      invalidatesTags: (r, _e, { planId }) => planWriteTags(planId, r?.plan.subject.personKey),
    }),
    approvePlan: builder.mutation<
      DecidePlanResult,
      { instanceId: string; planId: string; round?: number; step?: number }
    >({
      query: ({ instanceId, planId, round, step }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/approve`,
        method: "POST",
        params: { instanceId },
        body: { round, step },
      }),
      extraOptions: { maxRetries: 0 },
      // Spec 2026-10-13 §4: the last approval starts a plan below the final level (its to-dos).
      invalidatesTags: (r, _e, { planId }) => planWriteTags(planId, r?.plan.subject.personKey),
    }),
    sendBackPlan: builder.mutation<
      DecidePlanResult,
      { instanceId: string; planId: string; reason: string; round?: number; step?: number }
    >({
      query: ({ instanceId, planId, reason, round, step }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/send-back`,
        method: "POST",
        params: { instanceId },
        body: { reason, round, step },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { planId }) => [PLAN_LIST, planTag(planId)],
    }),
    reassignPlanStep: builder.mutation<
      DecidePlanResult,
      { instanceId: string; planId: string; round: number; step: number; personKey: string }
    >({
      query: ({ instanceId, planId, round, step, personKey }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/reassign-step`,
        method: "POST",
        params: { instanceId },
        body: { round, step, personKey },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { planId }) => [PLAN_LIST, planTag(planId)],
    }),
    // ── E3.b: activation, actions and comments ──
    /** After the meeting: the note, the ticks, version 1 and the to-dos in one transaction. */
    activatePlan: builder.mutation<
      ActivatePlanResult,
      { instanceId: string; planId: string; fields: ActivatePlanFields }
    >({
      query: ({ instanceId, planId, fields }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/activate`,
        method: "POST",
        params: { instanceId },
        body: fields,
      }),
      // Never re-sent: a lost answer may have activated it (a retry answers PLAN_NOT_APPROVED).
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { planId }) => planWriteTags(planId, r?.plan.subject.personKey),
    }),
    /** Spec 2026-10-13 §5: the subject's "I have read this plan", once, on an auto-started plan. */
    acknowledgePlan: builder.mutation<
      { plan: Plan },
      { instanceId: string; planId: string; fields: AcknowledgePlanFields }
    >({
      query: ({ instanceId, planId, fields }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/acknowledge`,
        method: "POST",
        params: { instanceId },
        body: fields,
      }),
      // Never re-sent: a lost answer may have stored it (a retry answers ALREADY_ACKNOWLEDGED).
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { planId }) => planWriteTags(planId, r?.plan.subject.personKey),
    }),
    /** A draft's action, or a running plan's with its to-do raised at once (acceptance 3). */
    addPlanAction: builder.mutation<
      AddPlanActionResult,
      { instanceId: string; planId: string; fields: AddPlanActionFields }
    >({
      query: ({ instanceId, planId, fields }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/actions`,
        method: "POST",
        params: { instanceId },
        body: fields,
      }),
      // Never re-sent: a lost answer would add the action (and its to-do) twice.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { planId }) => planWriteTags(planId, r?.plan.subject.personKey),
    }),
    /** The subject's own words (D10), append-only; only the plan changes. */
    addPlanComment: builder.mutation<
      PlanCommentResult,
      { instanceId: string; planId: string; body: string }
    >({
      query: ({ instanceId, planId, body }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/comments`,
        method: "POST",
        params: { instanceId },
        body: { body },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { planId }) => [planTag(planId)],
    }),
    // ── E3.c: reviews, the reminder, resolution, export ──
    /** A review's meeting notes (acceptance 1); moves "next" and Home's review due. */
    recordPlanReview: builder.mutation<
      { plan: Plan },
      { instanceId: string; planId: string; reviewNo: number; note: string }
    >({
      query: ({ instanceId, planId, reviewNo, note }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/reviews/${reviewNo}/record`,
        method: "POST",
        params: { instanceId },
        body: { note },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { planId }) => planWriteTags(planId, r?.plan.subject.personKey),
    }),
    /**
     * The reminder lead (W-3; Home's review due reads it) and/or the repeating reminder's gap
     * (spec 2026-10-13; null turns it off). Only the fields given are sent.
     */
    setPlanReminder: builder.mutation<
      { plan: Plan },
      { instanceId: string; planId: string } & PlanReminderFields
    >({
      query: ({ instanceId, planId, ...fields }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/reminder`,
        method: "PUT",
        params: { instanceId },
        body: fields,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { planId }) => planWriteTags(planId, r?.plan.subject.personKey),
    }),
    /**
     * Complete, extend or advance (acceptance 2-4). Advance opens a draft at the next level: the
     * list (in planWriteTags) and that draft, should it be cached, are refetched.
     */
    resolvePlan: builder.mutation<
      ResolvePlanResult,
      { instanceId: string; planId: string; fields: ResolvePlanFields }
    >({
      query: ({ instanceId, planId, fields }) => ({
        url: `/performance/plans/${encodeURIComponent(planId)}/resolve`,
        method: "POST",
        params: { instanceId },
        body: fields,
      }),
      // Never re-sent: a lost answer may have closed it, or opened the next draft.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { planId }) => [
        ...planWriteTags(planId, r?.plan.subject.personKey),
        ...(r?.advancedTo ? [planTag(r.advancedTo.id)] : []),
      ],
    }),
    /**
     * E3.c acceptance 5 (D20): the list's current tab and search as CSV, on E1's export pattern:
     * fetched on click, never cached, never re-sent (each one is audited).
     */
    exportPlans: builder.mutation<
      { csv: string; filename: string },
      Pick<PlansQuery, "instanceId" | "tab" | "subject" | "q">
    >({
      query: ({ instanceId, ...filters }) => ({
        url: "/performance/plans/export.csv",
        params: definedParams({ instanceId, ...filters }),
        responseHandler: (response) =>
          response.ok ? response.text() : response.json().catch(() => null),
      }),
      transformResponse: (csv: string, meta) => ({
        csv,
        filename:
          /filename="([^"]+)"/.exec(
            (meta as { response?: Response } | undefined)?.response?.headers.get(
              "Content-Disposition",
            ) ?? "",
          )?.[1] ?? "plans.csv",
      }),
      extraOptions: { maxRetries: 0 },
    }),
    // ── Annual evaluations (E4) ──
    /** A tab's rows with the counts; `subject` narrows to one person (the file's tab). */
    getEvaluations: builder.query<
      EvaluationList,
      { instanceId: string; tab?: EvaluationTab; subject?: string; cycle?: number }
    >({
      query: ({ instanceId, ...filters }) => ({
        url: "/performance/evaluations",
        params: definedParams({ instanceId, ...filters }),
      }),
      providesTags: [EVAL_LIST],
    }),
    /** One evaluation; every open is audited on the server. */
    getEvaluation: builder.query<
      { evaluation: Evaluation },
      { instanceId: string; evaluationId: string }
    >({
      query: ({ instanceId, evaluationId }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { evaluationId }) => [evalTag(evaluationId)],
    }),
    /** R4: who the caller may start an evaluation for. */
    getEvaluationSubjects: builder.query<
      { subjects: EvaluationSubjectCandidate[] },
      { instanceId: string; q?: string }
    >({
      query: ({ instanceId, q }) => ({
        url: "/performance/evaluations/subjects",
        params: definedParams({ instanceId, q }),
      }),
    }),
    startEvaluation: builder.mutation<
      { evaluation: Evaluation },
      { instanceId: string; subjectPersonKey: string; reviewOn: string; band: "year1" | "year2" }
    >({
      query: ({ instanceId, ...body }) => ({
        url: "/performance/evaluations",
        method: "POST",
        params: { instanceId },
        body,
      }),
      // Never re-sent: a lost answer would start it twice (the second is refused, R1).
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [EVAL_LIST],
    }),
    /** R5: the draft, saved as the evaluator works. Its own cache entry only. */
    saveEvaluationDraft: builder.mutation<
      { evaluation: Evaluation },
      { instanceId: string; evaluationId: string; patch: EvaluationDraftPatch }
    >({
      query: ({ instanceId, evaluationId, patch }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}`,
        method: "PUT",
        params: { instanceId },
        body: patch,
      }),
      extraOptions: { maxRetries: 0 },
      // The list's progress and score follow the draft (final review M3).
      invalidatesTags: [EVAL_LIST],
      // The answer is the evaluation; the page's cache takes it without a refetch.
      async onQueryStarted({ instanceId, evaluationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            performanceApi.util.upsertQueryData(
              "getEvaluation",
              { instanceId, evaluationId },
              data,
            ),
          );
        } catch {
          // The page shows the refusal; the cache keeps what it had.
        }
      },
    }),
    addEvaluationKpi: builder.mutation<
      { evaluation: Evaluation },
      { instanceId: string; evaluationId: string; name: string }
    >({
      query: ({ instanceId, evaluationId, name }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}/kpis`,
        method: "POST",
        params: { instanceId },
        body: { name },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { evaluationId }) => [EVAL_LIST, evalTag(evaluationId)],
    }),
    /** R6: an opportunity raises the employee's to-do, so the to-dos refetch too. */
    addEvaluationDevelopment: builder.mutation<
      { evaluation: Evaluation },
      { instanceId: string; evaluationId: string; text: string; how: string }
    >({
      query: ({ instanceId, evaluationId, text, how }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}/development`,
        method: "POST",
        params: { instanceId },
        body: { text, how },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { evaluationId }) => [
        EVAL_LIST,
        evalTag(evaluationId),
        TODO_LIST,
        TODO_COUNTS,
      ],
    }),
    /** #1433: a saved opportunity edited while in progress; its open to-do reads the same. */
    editEvaluationDevelopment: builder.mutation<
      { evaluation: Evaluation },
      {
        instanceId: string;
        evaluationId: string;
        developmentId: string;
        text: string;
        how: string;
      }
    >({
      query: ({ instanceId, evaluationId, developmentId, text, how }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}/development/${encodeURIComponent(developmentId)}`,
        method: "PUT",
        params: { instanceId },
        body: { text, how },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { evaluationId, developmentId }) => {
        // Its to-do's text follows (while open), so that to-do's own entry is read again too.
        const todoId = r?.evaluation.development.find((d) => d.id === developmentId)?.todoId;
        return [
          evalTag(evaluationId),
          TODO_LIST,
          ...(todoId ? [{ type: "Performance" as const, id: `TODO:${todoId}` }] : []),
        ];
      },
    }),
    /** R8: complete and sign; the subject's file and Home's counts change. */
    completeEvaluation: builder.mutation<
      { evaluation: Evaluation },
      { instanceId: string; evaluationId: string }
    >({
      query: ({ instanceId, evaluationId }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}/complete`,
        method: "POST",
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { evaluationId }) => [
        EVAL_LIST,
        evalTag(evaluationId),
        { type: "Performance" as const, id: "FILE" },
        ...(r
          ? [{ type: "Performance" as const, id: `FILE:${r.evaluation.subject.personKey}` }]
          : []),
      ],
    }),
    /** R9: the employee's signature. */
    acknowledgeEvaluation: builder.mutation<
      { evaluation: Evaluation },
      { instanceId: string; evaluationId: string }
    >({
      query: ({ instanceId, evaluationId }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}/acknowledge`,
        method: "POST",
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { evaluationId }) => [
        EVAL_LIST,
        evalTag(evaluationId),
        { type: "Performance" as const, id: "FILE" },
      ],
    }),
    /** R14: the tab as CSV, fetched on click, never cached, never re-sent (each is audited). */
    exportEvaluations: builder.mutation<
      { csv: string; filename: string },
      { instanceId: string; tab?: EvaluationTab }
    >({
      query: ({ instanceId, tab }) => ({
        url: "/performance/evaluations/export.csv",
        params: definedParams({ instanceId, tab }),
        responseHandler: (response) =>
          response.ok ? response.text() : response.json().catch(() => null),
      }),
      transformResponse: (csv: string, meta) => ({
        csv,
        filename:
          /filename="([^"]+)"/.exec(
            (meta as { response?: Response } | undefined)?.response?.headers.get(
              "Content-Disposition",
            ) ?? "",
          )?.[1] ?? "evaluations.csv",
      }),
      extraOptions: { maxRetries: 0 },
    }),
    // ── Site visits (E5) ──
    getVisits: builder.query<VisitList, { instanceId: string; tab?: VisitTab; limit?: number }>({
      query: ({ instanceId, tab, limit }) => ({
        url: "/performance/visits",
        params: definedParams({ instanceId, tab, limit }),
      }),
      providesTags: [VISIT_LIST],
    }),
    /** R4: where the caller may run a visit, its leaders, and the forms HR configured. */
    getVisitStartOptions: builder.query<VisitStartOptions, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/performance/visits/locations", params: { instanceId } }),
    }),
    getVisit: builder.query<{ visit: Visit }, { instanceId: string; visitId: string }>({
      query: ({ instanceId, visitId }) => ({
        url: `/performance/visits/${encodeURIComponent(visitId)}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { visitId }) => [visitTag(visitId)],
    }),
    startVisit: builder.mutation<
      { visit: Visit },
      {
        instanceId: string;
        location: string;
        leaderPersonKey: string;
        formKey: string;
        reviewMonth: string;
        visitOn?: string;
        /** HR only: who does the visit (ruling 2026-10-12). */
        reviewerPersonKey?: string;
      }
    >({
      query: ({ instanceId, ...body }) => ({
        url: "/performance/visits",
        method: "POST",
        params: { instanceId },
        body,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [VISIT_LIST],
    }),
    /** R5, Q3: the save as they walk; the answer lands in the visit's own cache entry. */
    saveVisit: builder.mutation<
      { visit: Visit },
      { instanceId: string; visitId: string; patch: VisitPatch }
    >({
      query: ({ instanceId, visitId, patch }) => ({
        url: `/performance/visits/${encodeURIComponent(visitId)}`,
        method: "PUT",
        params: { instanceId },
        body: patch,
      }),
      // The page's save queue retries (Q3); RTK never re-sends on its own.
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [VISIT_LIST],
      async onQueryStarted({ instanceId, visitId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(performanceApi.util.upsertQueryData("getVisit", { instanceId, visitId }, data));
        } catch {
          // The queue keeps the change and retries; the cache keeps what it had.
        }
      },
    }),
    /** HR hands an unrated visit to someone else (ruling 2026-10-12). */
    reassignVisit: builder.mutation<
      { visit: Visit },
      { instanceId: string; visitId: string; reviewerPersonKey: string }
    >({
      query: ({ instanceId, visitId, reviewerPersonKey }) => ({
        url: `/performance/visits/${encodeURIComponent(visitId)}/reviewer`,
        method: "PUT",
        params: { instanceId },
        body: { reviewerPersonKey },
      }),
      invalidatesTags: [VISIT_LIST],
      async onQueryStarted({ instanceId, visitId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(performanceApi.util.upsertQueryData("getVisit", { instanceId, visitId }, data));
        } catch {
          // The dialog shows the refusal; the cache keeps what it had.
        }
      },
    }),
    /** R9: one photo of an item (multipart). */
    addVisitPhoto: builder.mutation<
      { visit: Visit },
      { instanceId: string; visitId: string; itemKey: string; file: File }
    >({
      query: ({ instanceId, visitId, itemKey, file }) => {
        const form = new FormData();
        form.append("itemKey", itemKey);
        form.append("photo", file, file.name);
        return {
          url: `/performance/visits/${encodeURIComponent(visitId)}/photos`,
          method: "POST",
          params: { instanceId },
          body: form,
        };
      },
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { visitId }) => [visitTag(visitId)],
    }),
    /** A short-lived URL for one photo; fetched on click, never cached. */
    getVisitPhotoUrl: builder.mutation<
      { url: string; expiresAt: string },
      { instanceId: string; visitId: string; photoId: string }
    >({
      query: ({ instanceId, visitId, photoId }) => ({
        url: `/performance/visits/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}`,
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
    }),
    /** R7: an item added by hand, with its owner. */
    addVisitAction: builder.mutation<
      { visit: Visit },
      {
        instanceId: string;
        visitId: string;
        text: string;
        ownerPersonKey: string;
        dueOn?: string | null;
      }
    >({
      query: ({ instanceId, visitId, ...body }) => ({
        url: `/performance/visits/${encodeURIComponent(visitId)}/actions`,
        method: "POST",
        params: { instanceId },
        body,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { visitId }) => [visitTag(visitId)],
    }),
    /** R7: the plan to the to-do lists; raises to-dos, so they refetch. */
    sendVisitPlan: builder.mutation<
      { visit: Visit; sent: number },
      { instanceId: string; visitId: string; dueOn?: string }
    >({
      query: ({ instanceId, visitId, dueOn }) => ({
        url: `/performance/visits/${encodeURIComponent(visitId)}/send`,
        method: "POST",
        params: { instanceId },
        body: dueOn ? { dueOn } : {},
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { visitId }) => [
        VISIT_LIST,
        visitTag(visitId),
        TODO_LIST,
        TODO_COUNTS,
      ],
    }),
    /** R8: submit; files a record on the leader, so the log and the file refetch. */
    submitVisit: builder.mutation<
      { visit: Visit; recordNo: string },
      { instanceId: string; visitId: string }
    >({
      query: ({ instanceId, visitId }) => ({
        url: `/performance/visits/${encodeURIComponent(visitId)}/submit`,
        method: "POST",
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (r, _e, { visitId }) => [
        VISIT_LIST,
        visitTag(visitId),
        { type: "Performance" as const, id: "COACHING_LIST" },
        { type: "Performance" as const, id: "FILE" },
        ...(r ? [{ type: "Performance" as const, id: `FILE:${r.visit.leader.personKey}` }] : []),
      ],
    }),
    /** R12: the visit log as CSV, fetched on click, never cached, never re-sent (audited). */
    exportVisits: builder.mutation<
      { csv: string; filename: string },
      { instanceId: string; tab?: VisitTab }
    >({
      query: ({ instanceId, tab }) => ({
        url: "/performance/visits/export.csv",
        params: definedParams({ instanceId, tab }),
        responseHandler: (response) =>
          response.ok ? response.text() : response.json().catch(() => null),
      }),
      transformResponse: (csv: string, meta) => ({
        csv,
        filename:
          /filename="([^"]+)"/.exec(
            (meta as { response?: Response } | undefined)?.response?.headers.get(
              "Content-Disposition",
            ) ?? "",
          )?.[1] ?? "visits.csv",
      }),
      extraOptions: { maxRetries: 0 },
    }),
    // ── Records and the Document Centre (E8) ──
    getDocuments: builder.query<DocumentList, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/performance/documents", params: { instanceId } }),
      providesTags: [{ type: "Performance", id: "DOCUMENTS" }],
    }),
    /** One document's PDF, fetched on click; every open is audited. */
    getDocumentPdf: builder.mutation<
      { blob: Blob; filename: string },
      { instanceId: string; docId: string }
    >({
      query: ({ instanceId, docId }) => ({
        url: `/performance/documents/${encodeURIComponent(docId)}/pdf`,
        params: { instanceId },
        responseHandler: fileResponse,
      }),
      transformResponse: asFile("document.pdf"),
      extraOptions: { maxRetries: 0 },
    }),
    /** R6: one PDF or a ZIP, with the reason; logged once. */
    exportDocuments: builder.mutation<
      { blob: Blob; filename: string },
      { instanceId: string; docIds: string[]; reason: string }
    >({
      query: ({ instanceId, ...body }) => ({
        url: "/performance/documents/export",
        method: "POST",
        params: { instanceId },
        body,
        responseHandler: fileResponse,
      }),
      transformResponse: asFile("documents.zip"),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [EXPORT_LOG],
    }),
    getRecordsList: builder.query<
      RecordsList,
      {
        instanceId: string;
        person?: string;
        kind?: RecordsListRow["kind"];
        from?: string;
        to?: string;
        q?: string;
      }
    >({
      query: ({ instanceId, ...filters }) => ({
        url: "/performance/records",
        params: definedParams({ instanceId, ...filters }),
      }),
      providesTags: [{ type: "Performance", id: "RECORDS_LIST" }],
    }),
    exportRecordsList: builder.mutation<
      { blob: Blob; filename: string },
      {
        instanceId: string;
        reason: string;
        person?: string;
        kind?: RecordsListRow["kind"];
        from?: string;
        to?: string;
        q?: string;
      }
    >({
      query: ({ instanceId, ...body }) => ({
        url: "/performance/records/export",
        method: "POST",
        params: { instanceId },
        body,
        responseHandler: fileResponse,
      }),
      transformResponse: asFile("records.csv"),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [EXPORT_LOG],
    }),
    getExportLog: builder.query<ExportLog, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/performance/records/exports", params: { instanceId } }),
      providesTags: [EXPORT_LOG],
    }),
    /** R7: one person's whole file as one ZIP, with the reason; logged once. */
    exportEmployeeFile: builder.mutation<
      { blob: Blob; filename: string },
      { instanceId: string } & EmployeeFileExportRequest
    >({
      query: ({ instanceId, ...body }) => ({
        url: "/performance/records/file-export",
        method: "POST",
        params: { instanceId },
        body,
        responseHandler: fileResponse,
      }),
      transformResponse: asFile("file.zip"),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [EXPORT_LOG],
    }),
    // ── E4.b: the self-evaluation ──
    getSelfEvaluation: builder.query<SelfEvaluation, { instanceId: string; evaluationId: string }>({
      query: ({ instanceId, evaluationId }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}/self`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { evaluationId }) => [evalTag(evaluationId)],
    }),
    saveSelfEvaluation: builder.mutation<
      SelfEvaluation,
      {
        instanceId: string;
        evaluationId: string;
        patch: { scores?: Record<string, Array<number | null>>; comments?: string | null };
      }
    >({
      query: ({ instanceId, evaluationId, patch }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}/self`,
        method: "PUT",
        params: { instanceId },
        body: patch,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { evaluationId }) => [evalTag(evaluationId)],
    }),
    submitSelfEvaluation: builder.mutation<
      SelfEvaluation,
      { instanceId: string; evaluationId: string }
    >({
      query: ({ instanceId, evaluationId }) => ({
        url: `/performance/evaluations/${encodeURIComponent(evaluationId)}/self/submit`,
        method: "POST",
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_r, _e, { evaluationId }) => [evalTag(evaluationId), EVAL_LIST],
    }),
    // ── The bell (E10) ──
    getNotifications: builder.query<NotificationList, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/performance/notifications", params: { instanceId } }),
      providesTags: [{ type: "Performance", id: "NOTIFICATIONS" }],
    }),
    markNotificationRead: builder.mutation<void, { instanceId: string; id: string }>({
      query: ({ instanceId, id }) => ({
        url: `/performance/notifications/${encodeURIComponent(id)}/read`,
        method: "POST",
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [{ type: "Performance", id: "NOTIFICATIONS" }],
    }),
    markAllNotificationsRead: builder.mutation<void, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/performance/notifications/read-all",
        method: "POST",
        params: { instanceId },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: [{ type: "Performance", id: "NOTIFICATIONS" }],
    }),
  }),
});

export const {
  useGetPerformanceConfigQuery,
  useGetMyPerformanceScopeQuery,
  useGetMyPerformancePermissionsQuery,
  useGetPositionsQuery,
  useSavePositionsMutation,
  useRecomputeMutation,
  useSaveSettingsMutation,
  useGetPerformanceSettingsQuery,
  useGetRolloutLocationsQuery,
  useSaveRolloutMutation,
  useGetNotificationPrefsQuery,
  useSaveNotificationPrefsMutation,
  useGetPersonQuery,
  usePutPersonAccessMutation,
  usePutVisitsGrantMutation,
  useSetPersonManagerMutation,
  useGetCoachingSubjectsQuery,
  useGetCoachingRecordsQuery,
  useGetCoachingRecordQuery,
  useCreateCoachingRecordMutation,
  useLazyGetCoachingTeamQuery,
  useLazyGetCoachingAttachmentUrlQuery,
  useCorrectCoachingRecordMutation,
  useDeleteCoachingRecordMutation,
  useExportCoachingRecordsMutation,
  useRestoreCoachingRecordMutation,
  useAcknowledgeCoachingRecordMutation,
  useRemindCoachingRecordMutation,
  useGetPersonFileQuery,
  useGetOrgChartQuery,
  useGetTodosQuery,
  useGetTodoQuery,
  useGetTodoOwnersQuery,
  useCreateTodoMutation,
  useAddTodoNoteMutation,
  useCloseTodoMutation,
  useGetPlansQuery,
  useGetPlanQuery,
  useGetPlanSubjectsQuery,
  useGetPlanChainQuery,
  useGetPlanApproversQuery,
  useGetPlanEvidenceQuery,
  useLazyGetPlanEvidenceQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useSetPlanApproversMutation,
  useSavePlanTerminationMutation,
  useSubmitPlanMutation,
  useApprovePlanMutation,
  useSendBackPlanMutation,
  useReassignPlanStepMutation,
  useActivatePlanMutation,
  useAcknowledgePlanMutation,
  useAddPlanActionMutation,
  useAddPlanCommentMutation,
  useRecordPlanReviewMutation,
  useSetPlanReminderMutation,
  useResolvePlanMutation,
  useExportPlansMutation,
  useExportOrgChartMutation,
  useGetEvaluationsQuery,
  useGetEvaluationQuery,
  useGetEvaluationSubjectsQuery,
  useStartEvaluationMutation,
  useSaveEvaluationDraftMutation,
  useAddEvaluationKpiMutation,
  useAddEvaluationDevelopmentMutation,
  useEditEvaluationDevelopmentMutation,
  useCompleteEvaluationMutation,
  useAcknowledgeEvaluationMutation,
  useExportEvaluationsMutation,
  useGetVisitsQuery,
  useGetVisitStartOptionsQuery,
  useGetVisitQuery,
  useStartVisitMutation,
  useSaveVisitMutation,
  useReassignVisitMutation,
  useAddVisitPhotoMutation,
  useGetVisitPhotoUrlMutation,
  useAddVisitActionMutation,
  useSendVisitPlanMutation,
  useSubmitVisitMutation,
  useExportVisitsMutation,
  useGetDocumentsQuery,
  useGetDocumentPdfMutation,
  useExportDocumentsMutation,
  useGetRecordsListQuery,
  useExportRecordsListMutation,
  useGetExportLogQuery,
  useExportEmployeeFileMutation,
  useGetNotificationsQuery,
  useGetSelfEvaluationQuery,
  useSaveSelfEvaluationMutation,
  useSubmitSelfEvaluationMutation,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} = performanceApi;
